// Pure rules for the Employment & Engagements UI — tested without rendering.
import type {
  Engagement, EngagementStatus, EngagementTerms, RelationshipType, SameEntityTaxFinding,
} from './engagementsApi';

export const RELATIONSHIP_LABEL: Record<RelationshipType, string> = {
  employee: 'Employee',
  independent_contractor: 'Independent contractor',
};

export const STATUS_LABEL: Record<EngagementStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Awaiting approval',
  active: 'Active',
  suspended: 'Suspended',
  ended: 'Ended',
};

export type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'outline';

export const STATUS_VARIANT: Record<EngagementStatus, BadgeVariant> = {
  draft: 'neutral',
  pending_approval: 'info',
  active: 'success',
  suspended: 'warning',
  ended: 'outline',
};

export interface EngagementPermissions {
  manage: boolean;
  approve: boolean;
}

export type EngagementAction = 'submit' | 'approve' | 'reject' | 'suspend' | 'resume' | 'end' | 'edit';

/** What the viewer may do to this engagement, in the order buttons appear. */
export function actionsFor(e: Engagement, can: EngagementPermissions): EngagementAction[] {
  const out: EngagementAction[] = [];
  switch (e.status) {
    case 'draft':
      if (can.manage) out.push('submit', 'edit', 'end');
      break;
    case 'pending_approval':
      if (can.approve) out.push('approve', 'reject');
      if (can.manage) out.push('end');
      break;
    case 'active':
      if (can.manage) out.push('edit', 'suspend', 'end');
      break;
    case 'suspended':
      if (can.manage) out.push('resume', 'end');
      break;
    default:
      break;
  }
  return out;
}

/** Live first (primary on top), then drafts/pending, then ended — newest first within a group. */
export function sortEngagements(rows: Engagement[]): Engagement[] {
  const rank = (e: Engagement) =>
    e.status === 'active' || e.status === 'suspended' ? (e.is_primary ? 0 : 1) : e.status === 'ended' ? 3 : 2;
  return [...rows].sort((a, b) => rank(a) - rank(b) || (b.start_date || '').localeCompare(a.start_date || ''));
}

/** A short summary line for a card ("Analyst · full_time · permanent"). */
export function termsSummary(t: EngagementTerms, rel: RelationshipType): string {
  const parts = [t.position, rel === 'employee' ? t.employment_type : null, rel === 'employee' ? t.contract_type : null]
    .map((x) => (x || '').toString().trim())
    .filter(Boolean)
    .map((x) => x.replace(/_/g, ' '));
  return parts.join(' · ');
}

/** Count line for the section header — counts ONLY what the viewer can see (O-2). */
export function countLine(rows: Engagement[]): string {
  const live = rows.filter((e) => e.status === 'active' || e.status === 'suspended').length;
  if (rows.length === 0) return 'No engagements you can see';
  return `${live} live · ${rows.length} total`;
}

// ── wizard ──────────────────────────────────────────────────────────────────

export interface WizardForm {
  legal_entity_id: string;
  relationship_type: RelationshipType | '';
  start_date: string;
  position: string;
  employment_type: string;
  contract_type: string;
  work_schedule: string;
  contract_end_date: string;
}

export const EMPTY_WIZARD: WizardForm = {
  legal_entity_id: '',
  relationship_type: '',
  start_date: '',
  position: '',
  employment_type: '',
  contract_type: '',
  work_schedule: '',
  contract_end_date: '',
};

export type WizardStep = 'company' | 'relationship' | 'terms' | 'review';
export const WIZARD_STEPS: WizardStep[] = ['company', 'relationship', 'terms', 'review'];

export function wizardErrors(f: WizardForm, step: WizardStep): Record<string, string> {
  const e: Record<string, string> = {};
  if (step === 'company' || step === 'review') {
    if (!f.legal_entity_id) e.legal_entity_id = 'Choose the company.';
  }
  if (step === 'relationship' || step === 'review') {
    if (!f.relationship_type) e.relationship_type = 'Choose employee or independent contractor.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(f.start_date)) e.start_date = 'Enter the start date.';
  }
  if (step === 'terms' || step === 'review') {
    if (f.relationship_type === 'employee' && !f.employment_type) {
      e.employment_type = 'Choose the employment type — it is required before approval.';
    }
    if (f.contract_type === 'fixed_term' && !f.contract_end_date) {
      e.contract_end_date = 'A fixed-term contract needs an end date.';
    }
    if (f.contract_end_date && f.start_date && f.contract_end_date < f.start_date) {
      e.contract_end_date = 'The contract cannot end before it starts.';
    }
  }
  return e;
}

/** The create body. A contractor never carries employment fields (the server refuses them). */
export function wizardPayload(f: WizardForm, submit: boolean) {
  const employee = f.relationship_type === 'employee';
  const terms: EngagementTerms = {
    position: f.position.trim() || null,
    ...(employee
      ? {
          employment_type: f.employment_type || null,
          contract_type: f.contract_type || null,
          work_schedule: f.work_schedule || null,
        }
      : {}),
    contract_start_date: f.start_date || null,
    contract_end_date: f.contract_end_date || null,
  };
  for (const k of Object.keys(terms) as (keyof EngagementTerms)[]) if (terms[k] === null) delete terms[k];
  return {
    legal_entity_id: f.legal_entity_id,
    relationship_type: f.relationship_type as RelationshipType,
    start_date: f.start_date,
    terms,
    submit,
  };
}


/**
 * Multi-entity Phase 4: a SECONDARY employment (active employee engagement that
 * is not the primary) is paid by its own company's payroll — its payroll
 * subject id is the engagement id. The primary's pay lives on the Payroll tab;
 * a contractor's fee in Contractor & External Pay.
 */
export function isSecondaryEmployment(e: Engagement): boolean {
  return e.relationship_type === 'employee' && !e.is_primary && (e.status === 'active' || e.status === 'suspended');
}

/**
 * ME7 (owner ruling 2026-10-25): payroll reports each employee + contractor
 * pair at ONE company as a "Confirm tax treatment" finding — including a pair
 * that has since ended (its historical overlap). The card offers the
 * confirmation from these findings only, never from its own guess:
 *   open        → can be confirmed (for the finding's generation);
 *   provisional → one side is still a draft / awaiting approval;
 *   confirmed   → already confirmed for this generation.
 */
export function pairFindingState(f: SameEntityTaxFinding): 'open' | 'provisional' | 'confirmed' {
  if (f.status === 'confirmed') return 'confirmed';
  return f.provisional ? 'provisional' : 'open';
}
